/**
 * Les deux gestes que betool-sign adresse au BFF d'identité DU TENANT.
 *
 * OÙ VIT LE TROUSSEAU, ET POURQUOI PAS ICI
 * ---------------------------------------------------------------------------
 * Le trousseau est UNIQUE PAR TENANT et commun à toutes ses applications
 * (addendum 5, D1). Il vit scellé dans un cookie `HttpOnly` tenu par le BFF
 * d'identité, et betool-sign n'en tient AUCUN : construire un trousseau propre
 * à cette application recréerait exactement le défaut que la doctrine corrige
 * — une identité ajoutée ici serait invisible du widget ailleurs.
 *
 * Cette page ne voit donc jamais le scellé. Elle connaît deux choses :
 * l'ADRESSE d'un slot (`slotRef`, qui n'ouvre rien toute seule) et une PREUVE
 * courte, bornée par son `exp`.
 *
 * LES DEUX GESTES
 * ---------------------------------------------------------------------------
 *   1. REMETTRE un lancement : le portail beTool envoie ici avec `?lancement=`,
 *      le BFF consomme l'intention et émet le slot dans son trousseau ;
 *   2. ACHETER une preuve pour ce slot, à remettre à `POST /api/betool/session`.
 *
 * Tout le reste — liste des comptes, gestes du trousseau — appartient au
 * runtime du SDK. Ces deux-là sont des gestes de l'application hôte.
 */

const NOM_COOKIE_CSRF = 'betool-csrf'

/**
 * Origine du BFF d'identité du tenant. Vide = instance non raccordée.
 *
 * FIGÉE À LA COMPILATION, comme toute `VITE_*` : la poser sur le service
 * n'aurait aucun effet, le bundle est déjà écrit.
 */
const ORIGINE_BFF = (import.meta.env.VITE_BETOOL_IDENTITE_BFF ?? '') as string

export function origineBffIdentite(): string {
  return ORIGINE_BFF
}

export function estRaccordeeAIdentite(): boolean {
  return ORIGINE_BFF !== ''
}

/**
 * Lit le jeton CSRF du cookie non-HttpOnly posé par le BFF.
 *
 * `join('=')` et non `split('=')[1]` : la valeur peut contenir le séparateur,
 * et un découpage naïf la tronquerait EN SILENCE — le BFF répondrait alors 403
 * sur un jeton qui semble pourtant présent.
 */
export function lireJetonCsrf(): string | null {
  for (const paire of document.cookie.split(';')) {
    const [nom, ...reste] = paire.trim().split('=')
    if (nom === NOM_COOKIE_CSRF) {
      const valeur = reste.join('=')
      return valeur === '' ? null : valeur
    }
  }
  return null
}

/**
 * Garantit qu'un jeton CSRF est disponible, en l'AMORÇANT si besoin.
 *
 * LE DÉFAUT QUE CETTE FONCTION FERME — mesuré en production
 * ---------------------------------------------------------------------------
 * Le cookie `betool-csrf` est posé par une RÉPONSE du BFF : un crochet y
 * répond à tout GET qui arrive sans lui. Tant qu'aucune requête n'a été faite
 * vers le BFF depuis ce navigateur, le cookie n'existe donc pas — et la
 * première écriture, parfaitement légitime, part sans double-submit et se fait
 * refuser en `403 csrf_jeton`.
 *
 * Le cas est structurel pour une application servie par un AUTRE serveur que le
 * BFF : rien dans son chargement n'appelle le BFF. Le BFF le dit lui-même, à
 * propos de son propre frontend en développement : « le frontend fait
 * explicitement un GET /portail/trousseau — le 401 attendu pose le cookie tout
 * pareil ». C'est ce GET-là qu'on fait ici.
 *
 * La réponse est JETÉE. Elle porterait la liste des comptes du trousseau, qui
 * n'a rien à faire dans une page d'application : on ne vient chercher qu'un
 * en-tête `Set-Cookie`.
 */
async function assurerJetonCsrf(): Promise<string | null> {
  const connu = lireJetonCsrf()
  if (connu !== null) return connu
  if (ORIGINE_BFF === '') return null

  try {
    // `include` : sans les identifiants, le BFF ne pose pas le cookie sur une
    // requête qu'il traite comme anonyme et jetable.
    await fetch(`${ORIGINE_BFF}/portail/trousseau`, {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    })
  } catch {
    // Amorçage au mieux : si le BFF est injoignable, l'écriture qui suit
    // échouera de toute façon, et c'est ELLE qui portera le bon message.
  }
  const apresAmorcage = lireJetonCsrf()
  if (apresAmorcage === null) {
    // Le BFF a répondu, et le jeton reste illisible d'ici. Le cas connu : un
    // cookie `betool-csrf` PLUS ANCIEN, posé quand le BFF le limitait à sa
    // propre origine. Le BFF le reçoit — donc son crochet n'en repose pas — et
    // cette origine-ci ne peut pas le lire, faute du domaine partagé.
    //
    // Dit ICI, avant l'écriture : sinon le refus arrive sous la forme d'un
    // `403 csrf_jeton` qui ressemble à une attaque déjouée, et on cherche du
    // côté de la configuration du serveur pendant que la cause est un cookie
    // périmé dans UN navigateur.
    // Les NOMS des cookies visibles, jamais leurs valeurs : un jeton CSRF dans
    // un journal de navigateur finit dans la capture d'écran d'un ticket.
    // Sans cette ligne, « introuvable » ne distingue pas « aucun cookie ne
    // parvient jusqu'ici » de « le cookie est là mais sous un autre nom ».
    const nomsVisibles = document.cookie
      .split(';')
      .map((paire) => paire.trim().split('=')[0])
      .filter((nom) => nom !== undefined && nom !== '')
    console.warn(
      '[sign] jeton CSRF introuvable après amorçage du BFF. ' +
        `Cookies lisibles depuis cette origine : ${nomsVisibles.length > 0 ? nomsVisibles.join(', ') : '(aucun)'}.`,
    )
  }
  return apresAmorcage
}

/** En-têtes d'une ÉCRITURE au BFF : le double-submit, une fois le jeton acquis. */
function entetesEcriture(jeton: string | null): Record<string, string> {
  const entetes: Record<string, string> = { 'content-type': 'application/json' }
  // Sans le duo du double-submit, le BFF refuse en 403 avant tout échange.
  if (jeton !== null) entetes['x-betool-csrf'] = jeton
  return entetes
}

/**
 * Un REFUS du BFF, distinct d'une panne.
 *
 * La distinction n'est pas cosmétique : dire « lancement expiré » sur un 503
 * enverrait l'utilisateur refaire un geste qui ne changera rien, et masquerait
 * l'incident. Un refus se dit ; une panne se réessaie.
 */
export class LancementRefuseError extends Error {
  constructor(
    readonly statut: number,
    readonly code: string,
  ) {
    super(code === '' ? `lancement refusé (HTTP ${statut})` : code)
    this.name = 'LancementRefuseError'
  }
}

/** La fiche rendue par le BFF quand l'intention est consommée. */
export interface FicheLancement {
  slotRef: string
  identite: string
  displayName?: string | null
  email?: string | null
  sessionPublicId?: string | null
}

export async function remettreLancement(jeton: string): Promise<FicheLancement> {
  if (ORIGINE_BFF === '') throw new Error('instance non raccordée à l’identité beTool')

  const jetonCsrf = await assurerJetonCsrf()

  let reponse: Response
  try {
    reponse = await fetch(`${ORIGINE_BFF}/portail/session/lancement`, {
      method: 'POST',
      headers: entetesEcriture(jetonCsrf),
      // Le scellé est un cookie du BFF : sans `include`, la requête part sans
      // lui et le BFF ne peut pas savoir à quel trousseau rattacher le slot.
      credentials: 'include',
      body: JSON.stringify({ jeton }),
    })
  } catch (erreur) {
    throw new Error(
      `BFF d’identité injoignable (${erreur instanceof Error ? erreur.message : 'inconnu'})`,
    )
  }

  const brut = (await reponse.json().catch(() => null)) as Record<string, unknown> | null

  if (!reponse.ok) {
    if (reponse.status >= 400 && reponse.status < 500) {
      // Le BFF nomme son refus dans `error` (`csrf_origine`, `csrf_jeton`), le
      // moteur dans `code`. Ne lire que l'un des deux donne « (aucun) » sur la
      // moitié des refus — et c'est précisément sur ceux-là qu'on a besoin du
      // nom, parce qu'ils viennent de la configuration, pas de l'utilisateur.
      throw new LancementRefuseError(
        reponse.status,
        typeof brut?.code === 'string'
          ? brut.code
          : typeof brut?.error === 'string'
            ? brut.error
            : '',
      )
    }
    throw new Error(`lancement indisponible (HTTP ${reponse.status})`)
  }

  if (
    brut === null ||
    typeof brut.slotRef !== 'string' ||
    brut.slotRef === '' ||
    typeof brut.identite !== 'string' ||
    brut.identite === ''
  ) {
    // Un 200 hors contrat n'est pas un succès : c'est une panne déguisée, et
    // elle se refuse comme telle plutôt que de produire une session bancale.
    throw new Error('réponse de lancement illisible')
  }

  return {
    slotRef: brut.slotRef,
    identite: brut.identite,
    displayName: typeof brut.displayName === 'string' ? brut.displayName : null,
    email: typeof brut.email === 'string' ? brut.email : null,
    sessionPublicId: typeof brut.sessionPublicId === 'string' ? brut.sessionPublicId : null,
  }
}

/** Le porteur mort : le slot n'existe plus dans le trousseau, ou sa session est close. */
export class PorteurMortError extends Error {
  constructor(readonly code: 'slot_retire' | 'session_fermee' | 'session_invalide') {
    super(code)
    this.name = 'PorteurMortError'
  }
}

export interface PreuveCourte {
  accessToken: string
  expiresIn: number
}

/**
 * Achète UNE preuve courte pour un slot.
 *
 * Les 401 du BFF sont CODÉS, et le code doit traverser INTACT : lui seul
 * permet de dire « ce compte a été oublié ici » plutôt que « votre session a
 * expiré ». Les confondre produit des déconnexions en cascade dont personne ne
 * comprend la cause.
 */
export async function acheterPreuve(slotRef: string): Promise<PreuveCourte> {
  if (ORIGINE_BFF === '') throw new Error('instance non raccordée à l’identité beTool')

  const entetes: Record<string, string> = { 'x-betool-slot': slotRef }
  const jeton = await assurerJetonCsrf()
  if (jeton !== null) entetes['x-betool-csrf'] = jeton

  let reponse: Response
  try {
    reponse = await fetch(`${ORIGINE_BFF}/portail/session/preuve`, {
      method: 'POST',
      headers: entetes,
      credentials: 'include',
    })
  } catch (erreur) {
    throw new Error(
      `preuve courte indisponible (${erreur instanceof Error ? erreur.message : 'inconnu'})`,
    )
  }

  if (reponse.status === 401) {
    let code = ''
    try {
      const brut = (await reponse.json()) as Record<string, unknown>
      code = typeof brut?.code === 'string' ? brut.code : typeof brut?.error === 'string' ? brut.error : ''
    } catch {
      /* Corps illisible : le refus générique dit déjà l'essentiel. */
    }
    if (code === 'slot_retire') throw new PorteurMortError('slot_retire')
    if (code === 'session_fermee') throw new PorteurMortError('session_fermee')
    throw new PorteurMortError('session_invalide')
  }

  if (!reponse.ok) {
    // Une panne ne se déguise JAMAIS en session invalide : un BFF qui tousse
    // déconnecterait alors tout le monde, et le symptôme désignerait
    // l'authentification au lieu de l'incident.
    throw new Error(`preuve courte indisponible (HTTP ${reponse.status})`)
  }

  const brut = (await reponse.json().catch(() => null)) as Record<string, unknown> | null
  if (
    brut === null ||
    typeof brut.accessToken !== 'string' ||
    brut.accessToken === '' ||
    typeof brut.expiresIn !== 'number' ||
    brut.expiresIn <= 0
  ) {
    throw new Error('preuve courte illisible')
  }
  return { accessToken: brut.accessToken, expiresIn: brut.expiresIn }
}

/**
 * Identifiant de cette application au registre beTool.
 *
 * Constante du produit, pas un réglage : le registre l'a fixé à
 * l'enregistrement.
 */
export const CLIENT_ID = 'sign'

/**
 * Origine du MOTEUR d'identité — distincte de celle du BFF, et les deux sont
 * nécessaires.
 *
 *   * le MOTEUR sert le composant de connexion (`/api/plateforme/betool-login.js`)
 *     et vérifie les preuves ;
 *   * le BFF tient le trousseau du tenant et consomme les lancements.
 *
 * Les confondre donne un écran de connexion qui ne se charge pas, ou un
 * lancement qui part au mauvais endroit — deux pannes muettes.
 */
const ORIGINE_MOTEUR = (import.meta.env.VITE_BETOOL_IDENTITY_ORIGIN ?? '') as string

export function origineMoteurIdentite(): string {
  return ORIGINE_MOTEUR
}
